import React from "react";
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    Skeleton,
    Stack,
    ToggleButton,
    Tooltip,
    Typography
} from "@mui/material";
import {API_URL, DIGILIB_BASE_URL, DIGILIB_SCALER_URL} from "../../constants";
import InfoIcon from "@mui/icons-material/Info";
import {useHistory, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {makeRequest} from "../../services/requestService";
import HomeLayout from "../../components/HomeLayout/HomeLayout";
import HideImageIcon from '@mui/icons-material/HideImage';
import ImageIcon from '@mui/icons-material/Image';
import PageCard from "../../components/Cards/PageCard";
import NoResult from "../../components/Search/NoResult";
import {useSnackbar} from "notistack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

function InfoSkeleton() {
    return (
        <Stack m={2}>
            <Skeleton height={30} width={'70%'}/>
            <Skeleton width={'20%'}/>
            <Skeleton width={'50%'}/>
            <Skeleton width={'70%'}/>
            <Skeleton/>
        </Stack>
    );
}

export default function Title() {
    const {id} = useParams();
    const {t} = useTranslation('common');
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const [hideImg, setHideImg] = React.useState(false);
    const [title, setTitle] = React.useState(undefined);
    const [pages, setPages] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [searchValue, setSearchValue] = React.useState("");
    const SIZE = 30;

    const columnsAbout = [
        {id: 'id', label: 'ID', minWidth: 20},
        {id: 'shortName', label: t('main.pages.title.information.about.shortName'), minWidth: 100},
        {id: 'name', label: t('main.pages.title.information.about.name'), minWidth: 100},
        {id: 'description', label: t('main.pages.title.information.about.description'), minWidth: 100},
    ];

    React.useEffect(() => {
        document.title = t("main.title") + " - " + t("main.pages.title.title");
    }, [t]);

    const fetchPage = async (page = 0) => {
        let url;
        if (searchValue)
            url = `${API_URL}/pages/search?query=${searchValue}&page=${page}&size=${SIZE}&titleID=${id}`;
        else
            url = `${API_URL}/pages/bytitle/${id}?page=${page}&size=${SIZE}`;
        let response = await makeRequest(url);
        if (response.status === 200 && Array.isArray(response.data.data)) {
            if (page > 0)
                setPages(prevState => [...prevState, ...response.data.data]);
            else
                setPages(response.data.data);
            setTotalPages(response.data.totalPages);
            setCurrentPage(response.data.currentPage);
            return true;
        }
        if (response.status === 400 || response.status > 403) {
            if (searchValue) {
                enqueueSnackbar(t("main.components.search.error"), {variant: "error"});
            } else {
                if (response.status === 404)
                    history.replace("/error/notfound");
                else
                    history.push("/error");
            }
        }
        return false;
    }

    React.useEffect(() => {
        const fetchData = async () => {
            let response = await makeRequest(`${API_URL}/title/${id}`);
            if (response.status === 200) {
                setTitle(response.data);
            } else {
                return Promise.reject(new Error(response.status));
            }
            return await fetchPage();
        }

        setLoading(true);
        fetchData().then((loaded) => {
            setLoading(!loaded);
        }).catch((error) => {
            if (error === 400 || error > 403) {
                if (error === 404)
                    history.replace("/error/notfound");
                else
                    history.push("/error");
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    React.useEffect(() => {
        fetchPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);


    const handleSubmitSearch = (event) => {
        if (event.target)
            setSearchValue(event.target.value);
        else
            setSearchValue("");
    }

    function downloadTitle() {
        console.log("downloading...");
    }

    // RENDER

    const toolbarElms = (
        <Tooltip title={hideImg ? t("main.components.button.showImg") : t("main.components.button.hideImg")}>
            <span>
                <ToggleButton
                    size="small"
                    value="hide"
                    selected={hideImg}
                    onChange={() => setHideImg(prevState => !prevState)}
                    disabled={loading}
                >
                    {hideImg
                        ? <ImageIcon sx={{color: theme => theme.palette.text.secondary}}/>
                        : <HideImageIcon sx={{color: theme => theme.palette.text.secondary}}/>}
                </ToggleButton>
            </span>
        </Tooltip>
    );

    const content = !loading ? pages.map((page, index) => (
            <PageCard
                key={`card-page-${page.id}`}
                page={page}
                imgSrc={`${DIGILIB_BASE_URL}${DIGILIB_SCALER_URL}/${title.location}!${page.viewing}`}
                hideImg={hideImg}
            />
        ))
        : [<Stack key={"loading-collection"} alignItems="center" m={4}><CircularProgress size={80}/></Stack>];


    const aside = (
        <>
            <Card elevation={0}>
                <CardContent>
                    <Typography color="textSecondary" display="flex" alignItems="center" gutterBottom>
                        <InfoIcon fontSize="small" sx={{mr: 1}}/>
                        {" " + t('main.pages.home.informations.title')}
                    </Typography>
                    <Typography variant="body2" component="p">
                        {t('main.pages.home.informations.content')}
                    </Typography>
                </CardContent>
            </Card>
            {loading ? <InfoSkeleton/> : <Card elevation={0}>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Typography color="textSecondary" gutterBottom>
                                {t('main.pages.title.information.about.title')}
                            </Typography>
                        </Grid>
                        {columnsAbout.map((column) => (
                            <Grid item xs={12} key={column.id}>
                                <Typography variant="body2">
                                    {column.label + " : "}
                                    {column.format && typeof value === 'number' ? column.format(title[column.id]) : title[column.id]}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>}
        </>
    );

    const titleElem = (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
                <Typography ml={2} variant="h6" fontSize={22} fontWeight="bold" color="textSecondary" display="flex"
                            alignItems="center" gap={1}>
                    {t('main.pages.title.title')}
                </Typography>
            </Box>
            <Box>
                <Tooltip title={t("main.pages.title.actions.download.tooltip")} arrow>
                    <IconButton onClick={() => downloadTitle()}>
                        <FileDownloadIcon/>
                    </IconButton>
                </Tooltip>
            </Box>
        </Stack>
    )

    return (
        <HomeLayout
            titleElement={titleElem}
            content={content.length > 0 ? content : [<NoResult key={"collection-no-result"}/>]}
            aside={aside}
            toolbarElms={toolbarElms}
            onSubmit={handleSubmitSearch}
            onScrollHitBottom={() => fetchPage(currentPage + 1)}
            hasMoreData={loading ? false : currentPage + 1 < totalPages}
        />
    );
}